import React, { useState, useRef, useCallback, useEffect, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { TaskFull } from '@/redux/internal-services/workspaceApiExtensions';
import { useUpdateTaskStatusMutation, useClaimTaskMutation, useFollowTaskMutation, useUnfollowTaskMutation, useGetTaskFollowersQuery, useReassignTaskMutation, useGetRemindersQuery, useCreateReminderMutation, useGetTaskCommentsQuery, useAddTaskCommentMutation } from '@/redux/internal-services/workspaceApiExtensions';
import { useGetTenantUserByIdQuery, useListTenantUsersQuery, useListAssignableRolesQuery } from '@/redux/internal-services/onboardingApi';
import { useAppSelector } from '@/hooks';

function useDebounce<T>(value: T, ms: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debouncedValue;
}

// ─── Config ────────────────────────────────────────────────────────────────────

export const PRIORITY_CFG = {
  low:    { dot: 'bg-gray-400',  text: 'text-gray-500',  label: 'Low',    bar: 'bg-gray-300' },
  normal: { dot: 'bg-blue-400',  text: 'text-blue-600',  label: 'Normal', bar: 'bg-blue-300' },
  high:   { dot: 'bg-amber-400', text: 'text-amber-600', label: 'High',   bar: 'bg-amber-400' },
  urgent: { dot: 'bg-red-500',   text: 'text-red-600',   label: 'Urgent', bar: 'bg-red-400' },
} as const;

export const STATUS_CFG = {
  open:      { bg: 'bg-blue-50',  text: 'text-blue-700',  label: 'Open' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', label: 'Done' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500',  label: 'Cancelled' },
} as const;

export const TYPE_LABEL: Record<string, string> = {
  follow_up: 'Follow up', approval: 'Approval', review: 'Review',
};

const THRESHOLD = 80;

const IcoChevron = ({ open }: { open: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ─── Icons ─────────────────────────────────────────────────────────────────────

export const IcoTaskCheck = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 mt-0.5 ${className ?? ''}`}>
    <polyline points="9 11 12 14 22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);

export const IcoCalendar = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 mt-0.5 ${className ?? ''}`}>
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

// Icon shown next to a linked context (property/job/transaction/contact) — swapped by
// context_type so the card visually reads "this is a property" etc. without a "Link" label.
const CONTEXT_ICON_PATHS: Record<string, React.ReactNode> = {
  property: (
    <>
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 21 9 12 15 12 15 21"/>
    </>
  ),
  job: (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2"/>
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </>
  ),
  transaction: (
    <>
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </>
  ),
  contact: (
    <>
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </>
  ),
  task: (
    <>
      <polyline points="9 11 12 14 22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </>
  ),
};

export const ContextTypeIcon = ({ type, className }: { type?: string | null; className?: string }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`flex-shrink-0 ${className ?? ''}`}>
    {(type && CONTEXT_ICON_PATHS[type]) ?? (
      <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></>
    )}
  </svg>
);

const IcoCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IcoX = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ─── Inline expanded detail ────────────────────────────────────────────────────

export const ENTITY_ROUTE: Record<string, (id: string) => string> = {
  engagement:  (id) => `/chat?engagement=${id}`,
  property:    (id) => `/chat?property=${id}`,
  job:         (id) => `/chat?job=${id}`,
  transaction: (id) => `/chat?transaction=${id}`,
  contact:     (id) => `/chat?contact=${id}`,
};

function TaskInlineDetail({ task, currentUserId }: { task: TaskFull; currentUserId: string }) {
  const [reassignTask, { isLoading: reassigning }] = useReassignTaskMutation();
  const [createReminder, { isLoading: creatingReminder }] = useCreateReminderMutation();
  const [followTask] = useFollowTaskMutation();
  const [unfollowTask] = useUnfollowTaskMutation();
  const { data: followers = [] } = useGetTaskFollowersQuery(task.task_id);
  const { data: comments = [] } = useGetTaskCommentsQuery(task.task_id);
  const [addComment, { isLoading: addingComment }] = useAddTaskCommentMutation();
  const { data: assigneeUser } = useGetTenantUserByIdQuery(
    task.assignee_user_id!,
    { skip: !task.assignee_user_id || task.assignee_user_id === currentUserId },
  );

  const [showReassign, setShowReassign] = useState<'user' | 'queue' | false>(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [reminderDt, setReminderDt] = useState('');
  const [reminderError, setReminderError] = useState('');
  const [reassignError, setReassignError] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState('');
  const debouncedUserSearch = useDebounce(userSearch, 300);
  const assigneeRowRef = useRef<HTMLDivElement>(null);
  const queueRowRef = useRef<HTMLDivElement>(null);
  const reassignPopoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  // Server-side user search — avoids the 50-row client-side cap.
  const { data: usersRes } = useListTenantUsersQuery(
    showReassign === 'user'
      ? { page_size: 50, ...(debouncedUserSearch ? { search: debouncedUserSearch } : {}) }
      : { page_size: 0 },
    { skip: showReassign !== 'user' },
  );
  const { data: rolesRes } = useListAssignableRolesQuery();
  const { data: remindersRes } = useGetRemindersQuery({ source_entity_id: task.task_id, status: 'active' });

  const openPopover = (ref: React.RefObject<HTMLDivElement | null>, mode: 'user' | 'queue') => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const upward = spaceBelow < 200;
      setPopoverStyle({
        position: 'fixed',
        width: 160,
        zIndex: 9999,
        left: Math.min(rect.left, window.innerWidth - 168),
        ...(upward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    }
    setShowReassign(prev => prev === mode ? false : mode);
    if (showReassign) setUserSearch('');
  };

  const handleToggleReassign = () => openPopover(assigneeRowRef, 'user');
  const handleToggleQueue = () => openPopover(queueRowRef, 'queue');

  useEffect(() => {
    if (!showReassign) return;
    const handler = (e: MouseEvent) => {
      if (!reassignPopoverRef.current?.contains(e.target as Node) &&
          !assigneeRowRef.current?.contains(e.target as Node) &&
          !queueRowRef.current?.contains(e.target as Node)) {
        setShowReassign(false);
        setUserSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showReassign]);

  const allUsers = usersRes?.data ?? [];
  const allRoles = (rolesRes && 'data' in rolesRes ? rolesRes.data : rolesRes) ?? [];
  // Users come from the server already filtered by debouncedUserSearch — no client-side re-filter.
  const filteredUsers = allUsers;
  const filteredRoles = userSearch
    ? allRoles.filter(r => r.name.toLowerCase().includes(userSearch.toLowerCase()))
    : allRoles;
  const showSearch = showReassign === 'user' ? true : allRoles.length > 5;

  const isFollowing = followers.includes(currentUserId);
  const activeReminderCount = remindersRes?.data?.length ?? task.active_reminder_count;

  const isAssignedToMe = task.assignee_user_id === currentUserId;
  const assigneeName = task.assignee_user_id
    ? (isAssignedToMe ? 'You' : (assigneeUser?.name ?? assigneeUser?.email ?? 'Assigned'))
    : 'Unassigned';
  const rawName = isAssignedToMe ? 'You' : (assigneeUser?.name ?? assigneeUser?.email ?? '');
  const assigneeInitials = rawName
    ? rawName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div className="mt-0.5" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>

      {/* ── Single row: [avatar+name flex-1] [follow pill] [bell] ─── */}
      <div className="flex items-center gap-1">

        {/* Assignee display */}
        <div className="flex items-center gap-1 flex-1 min-w-0 px-1 py-0.5">
          <div className="w-4 h-4 rounded-full bg-brand-dark/15 flex items-center justify-center text-[7px] font-bold text-brand-dark flex-shrink-0 leading-none">
            {assigneeInitials}
          </div>
          <span className="text-[10px] font-medium text-ui-text truncate flex-1 leading-none">{assigneeName}</span>
          {reassigning && (
            <svg className="animate-spin text-brand-dark/40 flex-shrink-0" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
            </svg>
          )}
        </div>

        {/* Assign to user */}
        <div ref={assigneeRowRef} className="relative flex-shrink-0">
          <button
            title="Assign to person"
            onClick={handleToggleReassign}
            className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${showReassign === 'user' ? 'bg-brand-dark/10 text-brand-dark' : 'text-ui-text hover:bg-ui-hover'}`}
          >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
          </button>
        </div>

        {/* Assign to role queue */}
        <div ref={queueRowRef} className="relative flex-shrink-0">
          <button
            title="Assign to queue"
            onClick={handleToggleQueue}
            className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${showReassign === 'queue' ? 'bg-brand-dark/10 text-brand-dark' : 'text-ui-text hover:bg-ui-hover'}`}
          >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </button>
        </div>

        {/* Follow pill — count embedded */}
        <button
          onClick={async () => {
            try {
              if (isFollowing) await unfollowTask(task.task_id).unwrap();
              else await followTask(task.task_id).unwrap();
            } catch { /* follow state is non-critical, silently ignore */ }
          }}
          className={`flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full border flex-shrink-0 transition-colors ${
            isFollowing
              ? 'bg-brand-dark/10 border-brand-dark/20 text-brand-dark hover:bg-red-50 hover:border-red-200 hover:text-red-500'
              : 'border-[#EDEDED] text-ui-text3 hover:border-brand-dark/30 hover:text-brand-dark'
          }`}
        >
          {isFollowing ? (
            <>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {followers.length > 0 ? `${followers.length} Following` : 'Following'}
            </>
          ) : (
            <>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              {followers.length > 0 ? `${followers.length} Follow` : 'Follow'}
            </>
          )}
        </button>

        {/* Bell — amber+badge when active, muted when none */}
        <button
          onClick={() => setShowAddReminder(v => !v)}
          title={activeReminderCount > 0 ? `${activeReminderCount} reminder${activeReminderCount > 1 ? 's' : ''}` : 'Add reminder'}
          className="relative flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full transition-colors hover:bg-brand-dark/[0.04] group/bell"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className={activeReminderCount > 0 ? 'text-amber-500' : 'text-ui-text transition-colors'}
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          {activeReminderCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[10px] h-[10px] rounded-full bg-amber-500 text-white text-[7px] flex items-center justify-center font-bold px-0.5 leading-none">
              {activeReminderCount}
            </span>
          )}
        </button>
      </div>

      {showReassign && createPortal(
        <div ref={reassignPopoverRef} style={popoverStyle} className="rounded-xl border border-[#EDEDED] bg-ui-card shadow-lg overflow-hidden">
          {showSearch && (
            <div className="p-1.5 border-b border-[#EDEDED]">
              <input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Search..."
                autoFocus
                className="w-full text-[10px] px-2 py-0.5 rounded-md border border-[#EDEDED] bg-ui-input text-ui-text focus:outline-none"
              />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {showReassign === 'user' && filteredUsers.map(u => (
              <button
                key={u.user_id}
                onClick={async () => {
                  setReassignError('');
                  try {
                    await reassignTask({ id: task.task_id, assignee_user_id: u.user_id }).unwrap();
                    setShowReassign(false);
                    setUserSearch('');
                  } catch {
                    setReassignError('Could not reassign — try again.');
                  }
                }}
                className={`w-full text-left text-[10px] px-3 py-1.5 hover:bg-brand-dark/5 transition-colors text-ui-text flex items-center gap-2 ${
                  u.user_id === task.assignee_user_id ? 'font-semibold text-brand-dark bg-brand-dark/5' : ''
                }`}
              >
                <div className="w-4 h-4 rounded-full bg-brand-dark/10 flex items-center justify-center text-[8px] font-bold text-brand-dark flex-shrink-0">
                  {(u.name || u.email || '?')[0].toUpperCase()}
                </div>
                {u.name || u.email}
              </button>
            ))}
            {showReassign === 'queue' && filteredRoles.map(r => (
              <button
                key={r.id}
                onClick={() => { reassignTask({ id: task.task_id, queue_role_id: r.id }); setShowReassign(false); setUserSearch(''); }}
                className={`w-full text-left text-[10px] px-3 py-1.5 hover:bg-brand-dark/5 transition-colors text-ui-text flex items-center gap-2 ${
                  r.id === task.queue_role_id ? 'font-semibold text-brand-dark bg-brand-dark/5' : ''
                }`}
              >
                <div className="w-4 h-4 rounded-full bg-brand-dark/10 flex items-center justify-center flex-shrink-0">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-brand-dark">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                {r.name}
              </button>
            ))}
            {showReassign === 'user' && filteredUsers.length === 0 && (
              <p className="text-[10px] text-ui-text3 text-center py-3">{debouncedUserSearch ? 'No users found' : 'Type to search users'}</p>
            )}
            {showReassign === 'queue' && filteredRoles.length === 0 && (
              <p className="text-[10px] text-ui-text3 text-center py-3">No queues found</p>
            )}
          </div>
        </div>,
        document.body
      )}

      {reassignError && <p className="text-[9px] text-red-500 px-1 mt-0.5">{reassignError}</p>}

      {showAddReminder && (
        <div className="mt-1.5 px-1 space-y-1">
          <div className="flex items-center gap-1.5">
            <input
              type="datetime-local"
              value={reminderDt}
              onChange={e => setReminderDt(e.target.value)}
              style={{ fontSize: 10 }}
              className="flex-1 h-6 border border-[#EDEDED] rounded-lg px-2 bg-ui-input text-ui-text focus:outline-none focus:ring-1 focus:ring-brand-dark/30"
            />
            <button
              onClick={async () => {
                if (!reminderDt) return;
                setReminderError('');
                try {
                  await createReminder({
                    title: task.title,
                    dtstart: new Date(reminderDt).toISOString(),
                    source_entity_type: 'task',
                    source_entity_id: task.task_id,
                  }).unwrap();
                  setShowAddReminder(false);
                  setReminderDt('');
                } catch {
                  setReminderError('Could not set reminder — try again.');
                }
              }}
              disabled={!reminderDt || creatingReminder}
              className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-lg bg-brand-dark text-white font-semibold hover:bg-brand-dark/85 transition-colors disabled:opacity-50"
            >
              {creatingReminder ? '…' : 'Set'}
            </button>
          </div>
          {reminderError && <p className="text-[9px] text-red-500">{reminderError}</p>}
        </div>
      )}

      {/* ── Comments ─── */}
      <div className="mt-1 space-y-1">
        <div className="flex items-center gap-1.5 px-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text flex-shrink-0">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span className="text-[11px] font-medium text-ui-text flex-1">
            {comments.length > 0 ? `${comments.length} comment${comments.length > 1 ? 's' : ''}` : 'Comments'}
          </span>
          <button
            title={showCommentForm ? 'Close' : 'Add comment'}
            onClick={() => setShowCommentForm(v => !v)}
            className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
              showCommentForm ? 'bg-brand-dark/10 text-brand-dark' : 'text-ui-text3/40 hover:text-brand-dark hover:bg-brand-dark/[0.06]'
            }`}
          >
            {showCommentForm ? (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            )}
          </button>
        </div>

        {showCommentForm && (
          <div className="flex flex-col gap-1 bg-ui-subtle rounded-xl px-2 py-2">
            <textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Write a comment…"
              rows={2}
              autoFocus
              className="w-full text-[10px] border border-[#EDEDED] rounded-lg px-2 py-1.5 bg-ui-card text-ui-text resize-none focus:outline-none focus:ring-1 focus:ring-brand-dark/30"
            />
            <div className="flex justify-end">
              <button
                onClick={async () => {
                  if (!commentText.trim()) return;
                  await addComment({ taskId: task.task_id, content: commentText.trim() });
                  setCommentText('');
                  setShowCommentForm(false);
                }}
                disabled={!commentText.trim() || addingComment}
                className="flex-shrink-0 text-[10px] px-2.5 h-6 rounded-lg bg-brand-dark text-white font-semibold hover:bg-brand-dark/85 disabled:opacity-50 transition-colors"
              >
                {addingComment ? '…' : 'Post'}
              </button>
            </div>
          </div>
        )}

        {comments.length > 0 && (
          <div className="space-y-1">
            {comments.map((c) => (
              <div key={c.comment_id} className="text-[10px] bg-ui-subtle rounded-lg px-2 py-1.5">
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className="font-medium text-ui-text truncate">{c.author_name || 'Unknown'}</span>
                  <span className="text-ui-text3/50 shrink-0 whitespace-nowrap">{new Date(c.created_on).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <p className="text-ui-text leading-snug whitespace-pre-wrap">{c.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  task: TaskFull;
  onSelect?: (taskId: string) => void;
  expandedId?: string | null;
  onExpand?: (id: string | null) => void;
  compact?: boolean;
  /** Hides complete/cancel actions and disables swipe (e.g. permission_denied in workflow context). */
  disabled?: boolean;
  /** Tooltip for a padlock shown in place of action buttons (e.g. gate_enforced reason). */
  lockedReason?: string;
  /** Called with an error message when a status mutation fails (for callers that manage their own error UI). */
  onStatusError?: (msg: string) => void;
}

const TaskCard: React.FC<Props> = ({ task, onSelect, expandedId, onExpand, compact = false, disabled = false, lockedReason, onStatusError }) => {
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';
  const navigate = useNavigate();
  const [updateStatus, { isLoading: busy }] = useUpdateTaskStatusMutation();
  const [claimTask, { isLoading: claiming }] = useClaimTaskMutation();

  const isClaimable = task.assignee_user_id === null && !!task.queue_role_id;

  // Controlled expand: if parent passes expandedId, only one card opens at a time
  const [localExpanded, setLocalExpanded] = useState(false);
  const showMore = onExpand ? expandedId === task.task_id : localExpanded;

  useEffect(() => {
    if (showMore && onExpand && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMore]);
  const [dragX, setDragXState] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragXRef  = useRef(0);
  const startX    = useRef(0);
  const startY    = useRef(0);
  const moved     = useRef(false);
  const active    = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setDragX = useCallback((x: number) => { dragXRef.current = x; setDragXState(x); }, []);

  // Claimable or externally-disabled tasks cannot be swiped — claim first, or no permission.
  const isOpen = task.status === 'open' && !isClaimable && !disabled;

  // ── date helpers ─────────────────────────────────────────────────────────────
  const now  = new Date();
  const due  = task.due_date ? new Date(task.due_date) : null;
  const isOverdue = due && due < now && !['completed', 'cancelled'].includes(task.status);
  const dueStr = due
    ? due.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
        ...(due.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
      })
    : null;

  const pc = PRIORITY_CFG[task.priority as keyof typeof PRIORITY_CFG] ?? PRIORITY_CFG.normal;
  const sc = STATUS_CFG[task.status   as keyof typeof STATUS_CFG]     ?? { bg: 'bg-gray-50', text: 'text-gray-500', label: task.status };

  // ── actions ──────────────────────────────────────────────────────────────────
  const resolveDrag = useCallback(() => {
    active.current = false;
    setDragging(false);
    const dx = dragXRef.current;
    if (dx >= THRESHOLD) {
      setDragX(180);
      setTimeout(() => setDragX(0), 300);
      updateStatus({ id: task.task_id, status: 'completed' }).unwrap().catch((e) => onStatusError?.(String(e)));
    } else if (dx <= -THRESHOLD) {
      setDragX(-180);
      setTimeout(() => setDragX(0), 300);
      updateStatus({ id: task.task_id, status: 'cancelled' }).unwrap().catch((e) => onStatusError?.(String(e)));
    } else {
      setDragX(0);
    }
  }, [task.task_id, updateStatus, setDragX, onStatusError]);

  // ── touch ─────────────────────────────────────────────────────────────────────
  const onTouchStart = useCallback((e: TouchEvent) => {
    if (!isOpen) return;
    active.current = true;
    moved.current  = false;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    setDragging(true);
  }, [isOpen]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!active.current) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (!moved.current && Math.abs(dy) > Math.abs(dx) + 5) {
      active.current = false;
      setDragging(false);
      setDragX(0);
      return;
    }
    if (Math.abs(dx) > 5) moved.current = true;
    e.preventDefault();
    setDragX(Math.max(-140, Math.min(140, dx)));
  }, [setDragX]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   resolveDrag,  { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   resolveDrag);
    };
  }, [onTouchStart, onTouchMove, resolveDrag]);

  // ── mouse drag (desktop) ─────────────────────────────────────────────────────
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0 || !isOpen) return;
    active.current = true;
    moved.current  = false;
    startX.current = e.clientX;
    setDragging(true);

    const onMove = (ev: MouseEvent) => {
      if (!active.current) return;
      const dx = ev.clientX - startX.current;
      if (Math.abs(dx) > 5) moved.current = true;
      setDragX(Math.max(-140, Math.min(140, dx)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      resolveDrag();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [isOpen, resolveDrag, setDragX]);

  const handleClick = () => {
    if (moved.current) { moved.current = false; return; }
    if (onSelect) {
      onSelect(task.task_id);
    } else if (onExpand) {
      onExpand(expandedId === task.task_id ? null : task.task_id);
    } else {
      setLocalExpanded(v => !v);
    }
  };

  const triggerAction = useCallback((direction: 'complete' | 'cancel', e: React.MouseEvent) => {
    e.stopPropagation();
    const target = direction === 'complete' ? 200 : -200;
    setDragX(target);
    setTimeout(async () => {
      try {
        await updateStatus({ id: task.task_id, status: direction === 'complete' ? 'completed' : 'cancelled' }).unwrap();
      } catch (err) {
        onStatusError?.(String(err));
      }
      setDragX(0);
    }, 290);
  }, [task.task_id, updateStatus, setDragX, onStatusError]);

  // ── progress (0–1) for each direction ────────────────────────────────────────
  const rightP = dragX > 0 ? Math.min(1, dragX / THRESHOLD) : 0;
  const leftP  = dragX < 0 ? Math.min(1, -dragX / THRESHOLD) : 0;

  return (
    <div ref={containerRef} className="relative overflow-hidden rounded-2xl select-none touch-pan-y">

      {/* ── RIGHT swipe = Complete (green) ─── */}
      <div
        className="absolute inset-0 rounded-2xl flex items-center justify-start pl-5"
        style={{ background: '#22c55e', opacity: rightP > 0 ? 1 : 0 }}
      >
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            color: '#fff',
            opacity: rightP,
            transform: `scale(${0.7 + 0.3 * rightP})`,
            transformOrigin: 'left center',
          }}
        >
          <IcoCheck />
          <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>Complete</span>
        </div>
      </div>

      {/* ── LEFT swipe = Cancel (red) ─── */}
      <div
        className="absolute inset-0 rounded-2xl flex items-center justify-end pr-5"
        style={{ background: '#ef4444', opacity: leftP > 0 ? 1 : 0 }}
      >
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            color: '#fff',
            opacity: leftP,
            transform: `scale(${0.7 + 0.3 * leftP})`,
            transformOrigin: 'right center',
          }}
        >
          <IcoX />
          <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>Cancel</span>
        </div>
      </div>

      {/* ── CARD ─── */}
      <div
        onMouseDown={onMouseDown}
        onClick={handleClick}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          cursor: isOpen ? (dragging ? 'grabbing' : 'pointer') : 'default',
        }}
        className={`relative rounded-2xl border bg-ui-card overflow-hidden ${showMore ? 'border-brand-dark/25 shadow-md' : 'border-[#EDEDED]'}`}
      >
        {/* Busy overlay */}
        {busy && (
          <div className="absolute inset-0 z-10 rounded-2xl bg-ui-card/70 flex items-center justify-center">
            <svg className="animate-spin text-brand-dark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
            </svg>
          </div>
        )}

        {/* Priority bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${pc.bar}`} />

        <div className={compact ? 'pl-2 pr-2 pt-1 pb-1 ml-1' : 'pl-3 pr-3 pt-2 pb-2 ml-2'}>
          {/* Title + status + actions */}
          <div className={`flex items-center gap-1.5 ${compact ? 'mb-0' : 'mb-1.5'}`}>
            <IcoTaskCheck className={`${pc.text} ${compact ? 'mt-0' : ''}`} />
            <h3 className={`flex-1 font-semibold text-ui-text leading-snug min-w-0 ${compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-2'}`}>{task.title}</h3>
            {compact && dueStr && (
              <span className={`text-[9px] font-medium flex-shrink-0 ${isOverdue ? 'text-red-500' : 'text-ui-text'}`}>{dueStr}</span>
            )}
            <div className="flex-shrink-0 flex items-center gap-1">
              {/* Exclamation badge: permission_denied (disabled) OR gate_enforced (lockedReason).
                  Shown alongside the action buttons below, not instead of them — a gate-enforced
                  task is still meant to be completed (that's the point of the gate); disabled is
                  the only case that actually blocks the buttons, via isOpen below. */}
              {(disabled || lockedReason) && task.status === 'open' && (
                <span
                  title={lockedReason ?? 'You do not have permission to complete this task'}
                  className={`flex items-center justify-center rounded-full bg-red-50 text-red-500 flex-shrink-0 ${compact ? 'w-5 h-5' : 'w-6 h-6'}`}
                >
                  <svg width={compact ? 9 : 11} height={compact ? 9 : 11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="13"/>
                    <line x1="12" y1="16" x2="12" y2="16.01"/>
                  </svg>
                </span>
              )}
              {isOpen && (
                <>
                  <button
                    type="button"
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => triggerAction('cancel', e)}
                    title="Cancel task"
                    className={`flex items-center justify-center rounded-full bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 transition-colors ${compact ? 'w-5 h-5' : 'w-6 h-6'}`}
                  >
                    <svg width={compact ? 9 : 11} height={compact ? 9 : 11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => triggerAction('complete', e)}
                    title="Complete task"
                    className={`flex items-center justify-center rounded-full bg-green-50 text-green-500 hover:bg-green-100 hover:text-green-600 transition-colors ${compact ? 'w-5 h-5' : 'w-6 h-6'}`}
                  >
                    <svg width={compact ? 9 : 11} height={compact ? 9 : 11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                </>
              )}
              <span className={`font-bold rounded-full uppercase tracking-wide ${sc.bg} ${sc.text} ${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2.5 py-1'}`}>
                {sc.label}
              </span>
            </div>
          </div>

          {/* Meta row */}
          <div className="space-y-0.5">
            <div className={`flex items-center gap-1 overflow-hidden ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
              <div className="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
                {task.queue_role_id && (
                  <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-brand-light/30 border border-brand-dark/15 text-brand-dark/70">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Queue
                  </span>
                )}
                {/* Date only in non-compact (compact shows it in title row) */}
                {!compact && (
                  <span className={`flex-shrink-0 font-medium ${isOverdue ? 'text-red-500' : 'text-ui-text3'}`}>
                    {isOverdue ? 'Overdue · ' : ''}{dueStr ?? '—'}
                  </span>
                )}
                {/* Context link — hidden in compact (redundant when inside a property/engagement card) */}
                {!compact && task.context?.display_label && (
                  <>
                    <span className="text-ui-text3/40 flex-shrink-0">·</span>
                    {(() => {
                      const ctxHref = task.context!.entity_id
                        ? (ENTITY_ROUTE[task.context!.context_type]?.(task.context!.entity_id) ?? null)
                        : null;
                      return ctxHref ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(ctxHref); }}
                          onMouseDown={e => e.stopPropagation()}
                          className="truncate text-brand-dark/80 hover:text-brand-dark transition-colors min-w-0 text-left"
                        >
                          {task.context!.display_label}
                        </button>
                      ) : (
                        <span className="truncate text-ui-text3 min-w-0">{task.context!.display_label}</span>
                      );
                    })()}
                  </>
                )}
                {task.active_reminder_count > 0 && (
                  <span className="flex-shrink-0 flex items-center gap-0.5 text-amber-500 font-medium">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    {task.active_reminder_count}
                  </span>
                )}
              </div>
              {!compact && (
                <span className={`flex-shrink-0 flex items-center gap-1 ${pc.text}`}>
                  <span className={`rounded-full ${pc.dot} w-1.5 h-1.5`} />
                  <span className="font-medium">{pc.label}</span>
                  <IcoChevron open={showMore} />
                </span>
              )}
            </div>
            {isClaimable && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); claimTask(task.task_id); }}
                  disabled={claiming}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-dark text-white text-[10px] font-semibold hover:bg-brand-dark/85 transition-colors disabled:opacity-50"
                >
                  {claiming ? (
                    <svg className="animate-spin" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
                      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                  {claiming ? 'Claiming…' : 'Claim'}
                </button>
              </div>
            )}
          </div>

          {showMore && (
            <div className={compact ? 'mt-0.5 pt-0.5 border-t border-[#EDEDED]/50' : 'mt-1.5 pt-1.5 border-t border-[#EDEDED]/50'}>
              <TaskInlineDetail task={task} currentUserId={currentUserId} />
            </div>
          )}


        </div>
      </div>
    </div>
  );
};

export default TaskCard;
